import { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import type { CriticalMoment, CriticalMomentsReview } from "@chessla/shared";
import { fetchCriticalMoments } from "./critical-moments.api";

interface CriticalMomentsProps {
  gameId: string;
  initialFactId?: string | null;
  selectedFactId: string | null;
  revealEvidence: boolean;
  onLoad?: (review: CriticalMomentsReview) => void;
  onSelect: (moment: CriticalMoment) => void;
  compact?: boolean;
}

export const CriticalMoments = ({
  gameId,
  initialFactId = null,
  selectedFactId,
  revealEvidence,
  onLoad,
  onSelect,
  compact = false,
}: CriticalMomentsProps) => {
  const [review, setReview] = useState<CriticalMomentsReview | null>(null);
  useEffect(() => {
    let active = true;
    setReview(null);
    void fetchCriticalMoments(gameId)
      .then((value) => {
        if (!active) return;
        setReview(value);
        onLoad?.(value);
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, [gameId, onLoad]);
  const moments = useMemo(() => review?.moments ?? [], [review?.moments]);
  const selectedIndex = moments.findIndex(({ factId }) => factId === selectedFactId);
  const selectAt = useCallback(
    (index: number) => {
      const moment = moments[index];
      if (moment) onSelect(moment);
    },
    [moments, onSelect],
  );
  useEffect(() => {
    if (selectedFactId || !initialFactId) return;
    const moment = moments.find(({ factId }) => factId === initialFactId);
    if (moment) onSelect(moment);
  }, [initialFactId, moments, onSelect, selectedFactId]);
  useEffect(() => {
    if (selectedIndex < 0) return;
    const keyboard = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (target?.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(target?.tagName ?? "")) return;
      if (event.key !== "ArrowUp" && event.key !== "ArrowDown") return;
      event.preventDefault();
      selectAt(selectedIndex + (event.key === "ArrowDown" ? 1 : -1));
    };
    window.addEventListener("keydown", keyboard);
    return () => window.removeEventListener("keydown", keyboard);
  }, [selectAt, selectedIndex]);
  if (!review || moments.length === 0) return null;
  return (
    <Box component="section" aria-label="Coaching moments" sx={{ mb: 2, border: "1px solid", borderColor: "divider" }}>
      <Box sx={{ p: 1.5 }}>
        <Typography component="h2" sx={{ fontSize: "1.1rem", fontWeight: 900, textTransform: "uppercase" }}>
          Moments worth reviewing
        </Typography>
        {revealEvidence && (
          <Typography sx={{ mt: 0.5, color: "text.secondary", fontSize: ".75rem" }}>
            {review.deterministicSummary}
          </Typography>
        )}
      </Box>
      {moments.map((moment, index) => (
        <Button
          key={moment.factId}
          aria-label={`Moment ${index + 1}, move ${Math.ceil(moment.ply / 2)}${revealEvidence ? ` ${moment.playedSan}` : ""}`}
          aria-pressed={selectedFactId === moment.factId}
          onClick={() => onSelect(moment)}
          sx={{
            display:
              compact && selectedFactId !== moment.factId
                ? "none"
                : { xs: selectedFactId && selectedFactId !== moment.factId ? "none" : "flex", sm: "flex" },
            minHeight: 56,
            width: "100%",
            borderRadius: 0,
            justifyContent: "space-between",
            px: 1.5,
            borderTop: "1px solid",
            borderColor: "divider",
            color: "text.primary",
          }}
        >
          <span>
            Moment {index + 1} · move {Math.ceil(moment.ply / 2)}
          </span>
          <strong>{revealEvidence ? moment.playedSan : "Think first"}</strong>
        </Button>
      ))}
      {selectedIndex >= 0 && (
        <Box sx={{ display: "flex", gap: 1, p: 1 }}>
          <Button
            disabled={selectedIndex === 0}
            onClick={() => selectAt(selectedIndex - 1)}
            sx={{ minHeight: 44, borderRadius: 0 }}
          >
            Previous
          </Button>
          <Button
            disabled={selectedIndex === moments.length - 1}
            onClick={() => selectAt(selectedIndex + 1)}
            sx={{ minHeight: 44, borderRadius: 0 }}
          >
            Next
          </Button>
        </Box>
      )}
    </Box>
  );
};
